import {
  Address,
  AnalyticsEventProperty,
  ButtonComponentV2,
  ProcessTypeInput,
  Typenames,
  VxlButtonStyle,
} from '@varo/graphql-schema-types';
import { Href, router } from 'expo-router';
import { SegmentInterface } from 'services/analytics/segment/types';
import { isWeb } from 'shared/constants/platform';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { ErrorScreenType } from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { Environment } from 'shared/providers/environment.constants';
import { isCombinedError } from 'shared/utils/errors/combinedErrorUtils';
import { isCoreError } from 'shared/utils/errors/CoreError';
import { logError } from 'shared/utils/sentry/sentry';
import { CombinedError } from 'urql';

export enum TabaPayEntryFlowType {
  ExternalAccount = 'EXTERNAL_ACCOUNT',
  VaApplication = 'VA_APPLICATION',
  VaPayment = 'VA_PAYMENT',
  DCFFlow = 'DCF',
  AutoPay = 'AUTOPAY',
}

export enum TabapayPartialAuthType {
  PartialAuthTransaction = 'partial_auth_transaction',
  PartialAuthInsufficientFunds = 'partial_auth_insufficient_funds',
}

export const encryptWithRSa = async (
  base64UrlKey: string,
  data: string,
): Promise<Uint8Array> => {
  let base64 = base64UrlKey.replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4 !== 0) {
    base64 += '=';
  }

  const der = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
  const publicKey = await window.crypto.subtle.importKey(
    'spki',
    der.buffer,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256',
    },
    false,
    ['encrypt'],
  );
  const encoder = new TextEncoder();
  const encodedData = encoder.encode(data);
  const encrypted = await window.crypto.subtle.encrypt(
    { name: 'RSA-OAEP' },
    publicKey,
    encodedData,
  );
  return new Uint8Array(encrypted);
};

//Encryption for native from expo-tabapay module
export const encryptCardData = async (
  base64UrlKey: string,
  formattedCard: string,
): Promise<string> => {
  try {
    let base64: string = base64UrlKey.replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4 !== 0) {
      base64 += '=';
    }

    const { default: ExpoTabapay } = await import('@varo/expo-tabapay');

    const data = await ExpoTabapay.encryptData(formattedCard, base64);
    const encryptCardData = data
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
    return encryptCardData;
  } catch (error) {
    console.error(error);
    return '';
  }
};

export const encryptToken = async (
  rsaPublicKey: string,
  data: string,
): Promise<string> => {
  try {
    const encryptedBytes: Uint8Array = await encryptWithRSa(rsaPublicKey, data);

    const token = btoa(String.fromCharCode(...encryptedBytes))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');

    return token;
  } catch (error) {
    logError(error instanceof Error ? error.message : String(error));
    return '';
  }
};

export const TabaPayForm = {
  name: '',
  cardNumber: '',
  date: '',
  cvv: '',
  address: '',
};

export const getExternalAddressText = (address?: Address): string => {
  return address
    ? `${address?.addressLine1}, ${address?.addressLine2 ?? ''} ${address?.city}, ${address?.state} ${address?.zip}`
    : '';
};

export const checkError = (
  error: CombinedError | undefined,
  defaultTitle: string,
  defaultMessage: string,
) => {
  let errorTitle: string = defaultTitle;
  let errorMessage: string = defaultMessage;
  if (isCoreError(error)) {
    errorTitle = error.title;
    errorMessage = error.message;
  } else if (isCombinedError(error)) {
    errorMessage = error.message;
  }
  return {
    messageHeadline: errorTitle,
    messageBody: errorMessage,
  };
};

export const getFormattedCardData = (
  cardNumber: string,
  date: string,
  cvv: string,
): string | undefined => {
  const sanitizedCardNumber = cardNumber.replace(/\s+/g, '');
  const [month, year] = date.split('/');

  if (!month || !year) {
    return undefined;
  }
  const formattedDate = `20${year}${month}`;
  const finalCardData = `${sanitizedCardNumber}|${formattedDate}|${cvv}`;
  return finalCardData;
};

export const isProfileAddressSelected = (
  selectedAddress?: Address,
  profileAddress?: Address,
): boolean => {
  if (!selectedAddress || !profileAddress) {
    return false;
  }

  const keys = [
    'addressLine1',
    'addressLine2',
    'city',
    'state',
    'zip',
    'country',
    'externalId',
  ] as const;

  return keys.every((key) => selectedAddress[key] === profileAddress[key]);
};

type SetErrorScreenFn = React.Dispatch<
  React.SetStateAction<{
    screenType: ErrorScreenType.Error;
    messageHeadline: string;
    messageBody: string;
    primaryButton?: ButtonComponentV2;
    secondaryButton?: ButtonComponentV2;
    is3DSError?: boolean;
    processType?: string;
    isUserCancelled3DS?: boolean;
  } | null>
>;

type Handle3dsErrorScreenParams = {
  actionCode: string;
  setErrorScreen: SetErrorScreenFn;
  processType: ProcessTypeInput;
  flowName: string;
  translatedText: (key: string) => string;
  segmentClient: SegmentInterface;
  onSuccess?: () => Promise<void>;
  resolve?: (result: boolean) => void;
  setIsLoading: (result: boolean) => void;
  retryClickIdPrefix?: string;
  errorNumber: number;
  errorDescription: string;
};

export const getTabaPayEnv = (env: Environment): string => {
  switch (env) {
    case Environment.UAT:
      return 'staging';
    case Environment.PROD:
      return 'production';
    default:
      return 'staging';
  }
};

const buildAnalyticsProperties = (
  flowName: string,
  status: string,
): AnalyticsEventProperty[] => {
  return [
    {
      __typename: 'AnalyticsEventProperty',
      key: 'category',
      value: 'link_external_debit_card',
    },
    {
      __typename: 'AnalyticsEventProperty',
      key: 'flow_name',
      value: flowName,
    },
    {
      __typename: 'AnalyticsEventProperty',
      key: 'source_name',
      value: '3DS_challenge_outcome',
    },
    {
      __typename: 'AnalyticsEventProperty',
      key: 'status',
      value: status,
    },
  ];
};
export const log3DSErrorEvents = (
  flowName: string,
  status: string,
  segmentClient: SegmentInterface,
  errorNumber: number,
  errorDescription: string,
): void => {
  void segmentClient.logScreenEvent('3DS Challenge Outcome', {
    category: 'link_external_debit_card',
    flow_name: flowName,
    actionCode: status,
    errorNumber: errorNumber,
    errorDescription: errorDescription,
  });
  void segmentClient.logEvent('3DS Challenge Outcome', {
    category: 'link_external_debit_card',
    flow_name: flowName,
    actionCode: status,
    errorNumber: errorNumber,
    errorDescription: errorDescription,
  });
};

export const logTransactionAuthEvent = (
  subcategory: string,
  status: string,
  segmentClient: SegmentInterface,
  flowName?: string,
): void => {
  void segmentClient.logEvent('Transaction Auth Result', {
    category: 'Transaction',
    flow_name: flowName,
    subcategory: subcategory,
    status: status,
  });
};

export const logGenericErrorEvents = (
  category: string,
  status: string,
  segmentClient: SegmentInterface,
  code: string | undefined,
  messageBody: string | undefined,
  flowName?: string,
): void => {
  void segmentClient.logEvent('Error occurred', {
    api_name: category,
    flow_name: flowName,
    status: status,
    code: code,
    message: messageBody,
  });
};

const handleCancelLike3dsScreen = ({
  setErrorScreen,
  processType,
  flowName,
  translatedText,
  segmentClient,
  resolve,
  setIsLoading,
  errorNumber,
  errorDescription,
}: Handle3dsErrorScreenParams) => {
  log3DSErrorEvents(
    flowName,
    'cancel',
    segmentClient,
    errorNumber,
    errorDescription,
  );

  const analyticsProperties = buildAnalyticsProperties(flowName, 'cancel');

  setErrorScreen({
    screenType: ErrorScreenType.Error,
    messageHeadline: translatedText('errorMessage.cancelTitle'),
    messageBody: translatedText('errorMessage.cancelSubtitle'),
    is3DSError: true,
    isUserCancelled3DS: true,
    processType: processType,
    primaryButton: {
      id: 'CANCEL_BUTTON',
      buttonText: translatedText('errorMessage.continueButton'),
      buttonStyle: VxlButtonStyle.Primary,
      buttonOnClick: {
        id: 'CANCEL_BUTTON_CLICK',
        __typename: Typenames.SDContinueClientFlowAction,
        analyticsEvent: {
          __typename: 'AnalyticsEvent',
          name: 'Continue Clicked',
          properties: analyticsProperties,
        },
      },
    },
    secondaryButton: {
      id: 'EXIT_BUTTON',
      buttonText: translatedText('errorMessage.exitButton'),
      buttonStyle: VxlButtonStyle.Secondary,
      buttonOnClick: {
        id: 'EXIT_CLICK',
        __typename: Typenames.SDBackNavigationAction,
        analyticsEvent: {
          __typename: 'AnalyticsEvent',
          name: 'Debit Card Check Cancelled',
          properties: analyticsProperties,
        },
      },
    },
  });

  resolve?.(false);
  setIsLoading(false);
};

export const handle3dsActionCode = async ({
  actionCode,
  setErrorScreen,
  processType,
  flowName,
  translatedText,
  segmentClient,
  onSuccess,
  resolve,
  setIsLoading,
  retryClickIdPrefix = actionCode,
  errorNumber,
  errorDescription,
}: Handle3dsErrorScreenParams): Promise<void> => {
  switch (actionCode) {
    case 'SUCCESS':
    case 'NOACTION':
    case 'NO_ACTION': {
      log3DSErrorEvents(
        flowName,
        actionCode.toLowerCase(),
        segmentClient,
        errorNumber,
        errorDescription,
      );
      await onSuccess?.();
      resolve?.(true);
      return;
    }

    case 'TIMEOUT':
    case 'ERROR':
    case 'FAILURE': {
      let headlineKey: string;
      switch (actionCode) {
        case 'TIMEOUT':
          log3DSErrorEvents(
            flowName,
            actionCode.toLowerCase(),
            segmentClient,
            errorNumber,
            errorDescription,
          );
          headlineKey = 'errorMessage.timeOutTitle';
          break;
        case 'ERROR':
          log3DSErrorEvents(
            flowName,
            actionCode.toLowerCase(),
            segmentClient,
            errorNumber,
            errorDescription,
          );
          headlineKey = 'errorMessage.errorTitle';
          break;
        case 'FAILURE':
          headlineKey = 'errorMessage.failureTitle';
          if (isWeb) {
            handleCancelLike3dsScreen({
              translatedText,
              processType,
              flowName,
              segmentClient,
              setErrorScreen,
              resolve,
              setIsLoading,
              actionCode,
              errorNumber,
              errorDescription,
            });
            return;
          }
          break;
        default:
          log3DSErrorEvents(
            flowName,
            (actionCode as string).toLowerCase(),
            segmentClient,
            errorNumber,
            errorDescription,
          );
          headlineKey = 'errorMessage.failureTitle';
      }

      const analyticsProperties = buildAnalyticsProperties(
        flowName,
        actionCode.toLowerCase(),
      );

      setErrorScreen({
        screenType: ErrorScreenType.Error,
        messageHeadline: translatedText(headlineKey),
        messageBody:
          processType === ProcessTypeInput.CardRegistration
            ? translatedText('errorMessage.retryWithDifferentCard')
            : translatedText('errorMessage.tryAgain'),
        is3DSError: false,
        processType: processType,
        primaryButton: {
          id: `${retryClickIdPrefix}_BUTTON`,
          buttonText: translatedText('errorMessage.retryButton'),
          buttonStyle: VxlButtonStyle.Primary,
          buttonOnClick: {
            id: `${retryClickIdPrefix}_BUTTON_CLICK`,
            __typename: Typenames.SDContinueClientFlowAction,
            analyticsEvent: {
              __typename: 'AnalyticsEvent',
              name: 'Retry Clicked',
              properties: analyticsProperties,
            },
          },
        },
        ...(processType === ProcessTypeInput.CardRegistration
          ? {
              secondaryButton: {
                id: 'ADD_DIFFERENT_CARD',
                buttonText: translatedText('errorMessage.addDifferentCard'),
                buttonStyle: VxlButtonStyle.Secondary,
                buttonOnClick: {
                  id: 'ADD_DIFFERENT_CARD__BUTTON_CLICK',
                  __typename: Typenames.SDBackNavigationAction,
                  analyticsEvent: {
                    __typename: 'AnalyticsEvent',
                    name: 'Add Different Card Clicked',
                    properties: analyticsProperties,
                  },
                },
              },
            }
          : null),
      });
      resolve?.(false);
      setIsLoading(false);
      return;
    }

    case 'CANCEL': {
      handleCancelLike3dsScreen({
        translatedText,
        processType,
        flowName,
        segmentClient,
        setErrorScreen,
        resolve,
        setIsLoading,
        actionCode,
        errorNumber,
        errorDescription,
      });
      return;
    }
    default:
      resolve?.(false);
  }
};

export const logDebitCardCheckFailedEvents = (
  flowName: string,
  code: string,
  reason: string,
  activity_type: string,
  segmentClient: SegmentInterface,
): void => {
  void segmentClient.logEvent('Debit Card Check Failed', {
    card_type: 'debit_card',
    check_type: '3DS',
    error_code: code,
    failure_reason: reason,
    activity_type: activity_type,
    status: 'FAILED',
    flow_name: flowName,
  });
};

export const logDebitCardCheckPassedEvents = (
  activity_type: string,
  segmentClient: SegmentInterface,
  flowName: string,
): void => {
  void segmentClient.logEvent('Debit Card Check Passed', {
    card_type: 'debit_card',
    check_type: '3DS',
    activity_type: activity_type,
    status: 'PASSED',
    flow_name: flowName,
  });
};

export const logDebitCardCheckStepCompletedEvents = (
  step_name: string,
  flowName: string,
  segmentClient: SegmentInterface,
  activity_type?: string,
): void => {
  void segmentClient.logEvent('Debit Card Check Step Completed', {
    card_type: 'debit_card',
    check_type: '3DS',
    step_name: step_name,
    activity_type: activity_type,
    category: 'link_external_debit_card',
    flow_name: flowName,
  });
};

export const isUserCancelled3DS = (combinedErrorScreen: object): boolean => {
  return (
    combinedErrorScreen !== null &&
    typeof combinedErrorScreen === 'object' &&
    'isUserCancelled3DS' in combinedErrorScreen &&
    combinedErrorScreen.isUserCancelled3DS === true
  );
};

export const getFlowNameForAnalytics = (
  tabaPayEntryFlowType: TabaPayEntryFlowType,
): string => {
  switch (tabaPayEntryFlowType) {
    case TabaPayEntryFlowType.DCFFlow:
      return 'dcf';
    case TabaPayEntryFlowType.ExternalAccount:
      return 'card_management';
    default:
      return 'va_day_0';
  }
};

export const checkForbiddenChars = (text: string) => {
  return /[,"~^()]/.test(text);
};

export const getScreenName = (statusCode?: string): string => {
  switch (statusCode) {
    case TabapayPartialAuthType.PartialAuthTransaction:
      return 'Partial Auth Transaction';
    case TabapayPartialAuthType.PartialAuthInsufficientFunds:
      return 'Partial Auth Insufficient Funds';
    default:
      return '';
  }
};

export const handleActionRoute = (
  actionRoute: string | undefined,
  flow: string,
  tabaPayEntryFlowType: TabaPayEntryFlowType,
) => {
  if (!actionRoute) {
    navigateBack();
    return;
  }

  let finalRoute = actionRoute;

  if (actionRoute.startsWith('/move-money')) {
    finalRoute = actionRoute.replace(/^\/move-money/, `/${flow}`);
  }

  // Append tabaPayEntryFlowType query param
  finalRoute = `${finalRoute}?tabaPayEntryFlowType=${tabaPayEntryFlowType}`;

  router.replace(finalRoute as Href);
};
